import { Link, useNavigate } from "react-router-dom";
import { LegalFooter } from "@/components/LegalFooter";
import { useState } from "react";
import { useAuth } from "@/lib/authContext";
import { Sparkles, ArrowRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export default function SignupPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "", company_code: "" });
  const [mode, setMode] = useState("company");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!agreed) {
      toast.error("Please agree to the Terms of Service & Service Agreement");
      return;
    }
    setLoading(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        company: mode === "company" ? form.company : undefined,
        company_code: mode === "employee" ? form.company_code.trim().toUpperCase() : undefined,
        account_type: mode,
        terms_accepted: true,
      });
      toast.success("Account created!");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-50">
      <div className="hidden md:flex flex-col justify-between bg-zinc-900 text-white p-12 relative overflow-hidden">
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
        <Link to="/" className="flex items-center gap-2 relative" data-testid="signup-logo">
          <div className="w-8 h-8 bg-white rounded-md flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-zinc-900" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-xl">NextGen<span className="text-blue-400"> Resolution Experts</span></span>
        </Link>
        <div className="relative">
          <h2 className="font-display font-black tracking-tighter text-5xl leading-[0.95] mb-8">
            Join in<br />
            <span className="text-blue-400">60 seconds.</span>
          </h2>
          <ul className="space-y-3 text-zinc-300">
            {["Enterprise-grade AI support", "Your whole team, one account", "Unlimited AI chats from day one", "Cancel any time"].map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-blue-400" /> {f}</li>
            ))}
          </ul>
        </div>
        <div className="text-xs text-zinc-500 font-mono relative">No fluff. No sales calls. Just IT help.</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="md:hidden flex items-center gap-2 mb-12">
            <div className="w-8 h-8 bg-zinc-900 rounded-md flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-xl">NextGen<span className="text-blue-600"> Resolution Experts</span></span>
          </Link>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-2">Create account</div>
          <h1 className="font-display font-black tracking-tighter text-4xl mb-2">Let's get you set up.</h1>
          <p className="text-zinc-500 mb-10">Set up your company or join your team in under a minute.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <div className="flex gap-1.5 bg-zinc-100 rounded-xl p-1" data-testid="signup-mode-toggle">
              <button
                type="button"
                onClick={() => setMode("company")}
                className={`flex-1 px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition ${
                  mode === "company" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"
                }`}
                data-testid="signup-mode-company"
              >
                Create company account
              </button>
              <button
                type="button"
                onClick={() => setMode("employee")}
                className={`flex-1 px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition ${
                  mode === "employee" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"
                }`}
                data-testid="signup-mode-employee"
              >
                Join my team
              </button>
            </div>
            <div>
              <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Full name</label>
              <input
                required
                value={form.name}
                onChange={onChange("name")}
                placeholder="Alex Rivera"
                className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none"
                data-testid="signup-name"
              />
            </div>
            <div>
              <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Work email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={onChange("email")}
                placeholder="alex@company.com"
                className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none"
                data-testid="signup-email"
              />
            </div>
            {mode === "company" ? (
              <div>
                <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Company name</label>
                <input
                  required
                  value={form.company}
                  onChange={onChange("company")}
                  placeholder="Acme Co."
                  className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none"
                  data-testid="signup-company"
                />
                <p className="text-xs text-zinc-400 mt-1.5">You'll be the admin — you get a company code to share with your team.</p>
              </div>
            ) : (
              <div>
                <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Company code</label>
                <input
                  required
                  value={form.company_code}
                  onChange={onChange("company_code")}
                  placeholder="NGX-XXXXX"
                  className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm uppercase tracking-widest focus:border-blue-600 focus:outline-none"
                  data-testid="signup-company-code"
                />
                <p className="text-xs text-zinc-400 mt-1.5">Ask your company admin for the code (looks like NGX-4F7K2).</p>
              </div>
            )}
            <div>
              <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Password</label>
              <input
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={onChange("password")}
                placeholder="At least 8 characters"
                className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none"
                data-testid="signup-password"
              />
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer pt-1" data-testid="signup-terms-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-zinc-300 accent-blue-600 cursor-pointer"
                data-testid="signup-terms-checkbox"
              />
              <span className="text-xs text-zinc-500 leading-relaxed">
                I agree to the{" "}
                <Link to="/terms" target="_blank" className="text-blue-600 hover:underline font-medium">Terms of Service</Link>{" "}
                &{" "}
                <Link to="/service-agreement" target="_blank" className="text-blue-600 hover:underline font-medium">Service Agreement</Link>
              </span>
            </label>
            <button
              type="submit"
              disabled={loading || !agreed}
              className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 text-white font-bold py-4 rounded-full hover:bg-zinc-800 disabled:opacity-50 transition-all hover:-translate-y-0.5 mt-2"
              data-testid="signup-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create account <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
          <div className="mt-8 text-sm text-zinc-500 text-center">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-blue-600 hover:underline" data-testid="signup-to-login">
              Sign in
            </Link>
          </div>
          <LegalFooter />
        </div>
      </div>
    </div>
  );
}
